import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { CustomSyncService } from '../../core/services/custom-sync.service';
import { FormService } from '../../core/services/form.service';
import { FormSumbissionService } from '../../core/services/formSubmission.service';
import { LoggerService } from '../../core/services/logger.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardActionsComponent } from '../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { EnvPrefixPipe } from '../../shared/pipes/env-prefix.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-home',
  imports: [
    RouterLink,
    TranslatePipe,
    EnvPrefixPipe,
    CardComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ButtonComponent,
  ],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit {
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private interactionService = inject(FormSumbissionService);
  private translate = inject(TranslationService);
  private userStorage = inject(UserStorageService);
  private syncService = inject(CustomSyncService);
  private logger = inject(LoggerService).getLogger('home');

  showOnboardingLink = signal(false);

  async ngOnInit() {
    const homeInitTime = performance.now();
    this.logger.debug(`T+${(performance.now() - homeInitTime).toFixed(0)}ms 🏠 ngOnInit() called`);

    // CRITICAL: Wait for sync service to be ready before querying database
    this.logger.debug(
      `T+${(performance.now() - homeInitTime).toFixed(0)}ms ⏳ Waiting for sync to be ready...`,
    );
    const syncConnected = await this.syncService.connected();
    this.logger.debug(
      `T+${(performance.now() - homeInitTime).toFixed(0)}ms 🔌 Sync connected: ${syncConnected}`,
    );

    if (!syncConnected) {
      this.logger.warn('Sync not connected - cannot load dashboard data');
      return;
    }

    this.logger.debug(
      `T+${(performance.now() - homeInitTime).toFixed(0)}ms 📊 Loading dashboard data...`,
    );

    // Get the setup status from global config (accessible by all users)
    const hasCompletedSetup =
      this.userStorage.getGlobalTypedItem<boolean>('setup_completed') ?? false;

    // Check if user has already completed initial setup
    const checkPoints = await this.checkPointService.getAll();
    const formSubmission = await this.interactionService.getAll();
    const hasActualData = checkPoints.length > 0 && formSubmission.length > 0;

    // Auto-dismiss onboarding if user has data
    if (hasActualData) {
      this.userStorage.setTypedItem('onboarding_dismissed', true);
    }

    // Redirect to onboarding if user hasn't dismissed it AND hasn't completed setup
    const onboardingDismissed =
      this.userStorage.getTypedItem<boolean>('onboarding_dismissed') ??
      this.userStorage.getGlobalTypedItem<boolean>('onboarding_dismissed') ??
      false;
    if (!onboardingDismissed && !hasActualData) {
      this.router.navigate(['/onboarding']);
      return;
    }

    // Check if first two onboarding steps are complete
    await this.checkOnboardingStatus();
  }

  async checkOnboardingStatus(): Promise<void> {
    // Check if user has created asset and logged interaction
    const checkPoints = await this.checkPointService.getAll();
    const formSubmissions = await this.interactionService.getAll();

    const hasCheckPoints = checkPoints.length > 0;
    const hasInteractions = formSubmissions.length > 0;

    // Show onboarding link if first two steps aren't complete
    this.showOnboardingLink.set(!hasCheckPoints || !hasInteractions);
  }
}
